import React, { useCallback, useEffect, useState } from 'react';
import {
  useDropzone,
  FileRejection as DropZoneFileRejection,
} from 'react-dropzone';
import styled from 'styled-components';
import { token } from '@zillow/constellation';

import {
  acceptRejectFiles,
  FILE_INVALID_TYPE,
  FILE_TOO_LARGE,
  fileCountInRange,
  TOO_MANY_FILES,
} from '../lib/react-dropzone-utils';
import { FileRejections, RejectionErrorCode } from '../lib/error';

// eslint-disable-next-line @typescript-eslint/no-empty-function
function noop() {}

const DropArea = styled.div`
  position: relative;
  outline: none;
`;

export const DropZoneOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${token('colors.blue400')};
  opacity: 25%;
`;

function toErrorCode(str: string): RejectionErrorCode {
  switch (str) {
    case FILE_INVALID_TYPE:
      return RejectionErrorCode.InvalidType;
    case FILE_TOO_LARGE:
      return RejectionErrorCode.ExceededMaxSize;
    case TOO_MANY_FILES:
      return RejectionErrorCode.ExceededMaxFiles;
    default:
      return RejectionErrorCode.Unknown;
  }
}

function toFileRejections(
  rejections: DropZoneFileRejection[],
  hasAccepts: boolean
): FileRejections {
  return {
    partial: hasAccepts,
    rejections: rejections.map(r => {
      return {
        file: r.file,
        errorCodes: r.errors.map(e => toErrorCode(e.code)),
      };
    }),
  };
}

export type OnAddFilesClickType = {
  (e: React.MouseEvent<HTMLElement>): void;
};

export type DropZoneRendererProps = {
  isDragOutside: boolean;
  isDragInside: boolean;
  onAddFiles: OnAddFilesClickType;
};

export type FileConstraintProps = {
  /** Maximum file size in bytes. Defaults to zDocs max (100,000,000). */
  maxSize?: number;
  /** Maximum number of files allowed to upload */
  maxFiles?: number;
};

export type DropZoneConsumableProps = {
  /** File type filter. Same format as HTML `input`. */
  accept?: string | string[];
} & FileConstraintProps;

export type DropZoneSelectionProps = {
  onFileAccepted<T extends File>(files: T[]): void;
  onFileRejected?(rejections: FileRejections): void;
};

export type Props = {
  renderer: (rendererProps: DropZoneRendererProps) => React.ReactNode;
  className?: string;
} & DropZoneConsumableProps &
  DropZoneSelectionProps;

function DropZone({
  onFileAccepted,
  onFileRejected,
  renderer,
  accept,
  maxSize,
  maxFiles = Infinity,
  className,
}: Props) {
  // Paste Support
  const onPaste = useCallback(
    (files: File[]) => {
      const { acceptedFiles, fileRejections } = acceptRejectFiles(
        files,
        accept,
        maxSize,
        maxFiles
      );

      if (!!onFileAccepted && acceptedFiles.length > 0) {
        onFileAccepted(acceptedFiles);
      }
      if (!!onFileRejected && fileRejections.length > 0) {
        onFileRejected(
          toFileRejections(fileRejections, acceptedFiles.length > 0)
        );
      }
    },
    [onFileAccepted, onFileRejected, accept, maxSize, maxFiles]
  );

  useEffect(() => {
    function onFilePaste(evt: Event) {
      // Typescript issue prevents the function from using the proper event types.
      // See: https://github.com/microsoft/TypeScript/issues/5363
      //      https://github.com/Microsoft/TypeScript/wiki/Breaking-Changes
      if (evt instanceof ClipboardEvent) {
        const evtFiles = evt?.clipboardData?.files || [];
        const files =
          evtFiles instanceof FileList ? Array.from(evtFiles) : evtFiles;
        onPaste(files);
      }
    }

    window.addEventListener('paste', onFilePaste);
    return () => {
      window.removeEventListener('paste', onFilePaste);
    };
  }, [onPaste]);

  // Window Dragging Support
  const [isWindowDrag, setWindowDrag] = useState(false);

  useEffect(() => {
    function onDocumentDragOver() {
      setWindowDrag(true);
    }
    function onDocumentDragLeave() {
      setWindowDrag(false);
    }

    window.addEventListener('dragover', onDocumentDragOver, false);
    window.addEventListener('dragleave', onDocumentDragLeave, false);
    window.addEventListener('drop', onDocumentDragLeave, false);
    return () => {
      window.removeEventListener('dragover', onDocumentDragOver);
      window.removeEventListener('dragleave', onDocumentDragLeave);
      window.removeEventListener('drop', onDocumentDragLeave);
    };
  }, []);

  const onDrop = useCallback(
    (acceptedFiles, fileRejections) => {
      const [fileCountAccepted, fileCountError] = fileCountInRange(
        acceptedFiles,
        maxFiles
      );

      if (!fileCountAccepted) {
        const fileCountRejections = acceptedFiles.map((file: File) => {
          return { file, errors: [fileCountError] };
        });

        acceptedFiles = [];
        fileRejections = fileRejections.concat(fileCountRejections);
      }

      if (!!onFileAccepted && acceptedFiles.length > 0) {
        onFileAccepted(acceptedFiles);
      }

      if (!!onFileRejected && fileRejections.length > 0) {
        onFileRejected(
          toFileRejections(fileRejections, acceptedFiles.length > 0)
        );
      }
    },
    [onFileAccepted, onFileRejected, maxFiles]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: maxFiles > 1,
    accept,
    maxSize,
    maxFiles,
  });

  const isDragOutside = isWindowDrag && !isDragActive;
  const isDragInside = isDragActive;

  const rootProps = getRootProps();
  const inputProps = { ...getInputProps() };

  return (
    <DropArea
      {...rootProps}
      /* Override the click handler. Only fire on button click. */
      onClick={noop}
      className={className}
    >
      <input {...inputProps} />
      {renderer({
        isDragOutside,
        isDragInside,
        onAddFiles: rootProps?.onClick ?? noop,
      })}
    </DropArea>
  );
}

export default DropZone;
